import { useEffect, useState, useRef } from "react";

/**
 * Generic inventory hook matching your project's API response shapes:
 *   - fetchCars  → { cars: [], totalPages: 1 }
 *   - fetchSpares / fetchAccessories / fetchMechanics → plain array
 *
 * Usage:
 *   const { data, loading, error, reload } = useInventory(fetchSpares);
 *   const { data, loading, error, reload } = useInventory(() => fetchCars({ type: "vehicle" }, 1, 100));
 */

const extractList = (result) => {
  if (Array.isArray(result)) return result;
  if (Array.isArray(result?.cars)) return result.cars;
  if (Array.isArray(result?.data)) return result.data;
  return [];
};

export function useInventory(fetchFn) {
  const [data,    setData]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);
  const [token,   setToken]   = useState(0);

  // Keep fetchFn stable — avoid re-fetch loops when inline arrow passed
  const fetchRef = useRef(fetchFn);
  fetchRef.current = fetchFn;

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);

    fetchRef.current()
      .then((result) => {
        if (!active) return;
        setData(extractList(result));
      })
      .catch((err) => {
        if (!active) return;
        setError(err.message || "Failed to load data.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => { active = false; };
  }, [token]);

  const reload = () => setToken((t) => t + 1);

  return { data, loading, error, reload };
}